import type { GasMix, Waypoint } from "@decopl/deco-engine";
import { create } from "zustand";
import type { Lang } from "./i18n.js";

export type Theme = "dark" | "light";

export interface UIState {
  waypoints: Waypoint[];
  gases: GasMix[];
  gfLow: number;
  gfHigh: number;
  ascentRate: number;
  descentRate: number;
  lastStopDepth: number;
  stopIncrement: number;
  sac: number;
  tankVolume: number;
  tankStartPressure: number;
  theme: Theme;
  lang: Lang;
  audio: boolean;
  showDepth: boolean;
  showCeiling: boolean;
  showTankPressure: boolean;
  setWaypoint: (i: number, wp: Partial<Waypoint>) => void;
  addWaypoint: () => void;
  removeWaypoint: (i: number) => void;
  setGas: (i: number, g: Partial<GasMix>) => void;
  addGas: () => void;
  removeGas: (i: number) => void;
  setGF: (low: number, high: number) => void;
  setRates: (asc: number, desc: number) => void;
  setStops: (last: number, inc: number) => void;
  setTank: (sac: number, volume: number, startPressure: number) => void;
  setTheme: (t: Theme) => void;
  setLang: (l: Lang) => void;
  setAudio: (b: boolean) => void;
  toggle: (k: "showDepth" | "showCeiling" | "showTankPressure") => void;
}

export const useStore = create<UIState>((set) => ({
  waypoints: [{ depth: 30, duration: 25 }],
  gases: [{ fO2: 0.21, fN2: 0.79 }],
  gfLow: 30,
  gfHigh: 80,
  ascentRate: 9,
  descentRate: 18,
  lastStopDepth: 3,
  stopIncrement: 3,
  sac: 20,
  tankVolume: 12,
  tankStartPressure: 200,
  theme: "dark",
  lang: "fr",
  audio: false,
  showDepth: true,
  showCeiling: true,
  showTankPressure: true,
  setWaypoint: (i, wp) =>
    set((s) => ({
      waypoints: s.waypoints.map((w, idx) => (idx === i ? { ...w, ...wp } : w)),
    })),
  addWaypoint: () =>
    set((s) => ({ waypoints: [...s.waypoints, { depth: 6, duration: 3 }] })),
  removeWaypoint: (i) =>
    set((s) => ({ waypoints: s.waypoints.filter((_, idx) => idx !== i) })),
  setGas: (i, g) =>
    set((s) => ({
      gases: s.gases.map((gas, idx) => {
        if (idx !== i) return gas;
        const next = { ...gas, ...g };
        if (g.fO2 !== undefined) next.fN2 = Math.max(0, 1 - g.fO2 - (next.fHe ?? 0));
        return next;
      }),
    })),
  addGas: () => set((s) => ({ gases: [...s.gases, { fO2: 0.5, fN2: 0.5 }] })),
  removeGas: (i) =>
    set((s) => ({ gases: s.gases.filter((_, idx) => idx !== i) })),
  setGF: (low, high) => set({ gfLow: low, gfHigh: high }),
  setRates: (asc, desc) => set({ ascentRate: asc, descentRate: desc }),
  setStops: (last, inc) => set({ lastStopDepth: last, stopIncrement: inc }),
  setTank: (sac, volume, startPressure) =>
    set({ sac, tankVolume: volume, tankStartPressure: startPressure }),
  setTheme: (theme) => set({ theme }),
  setLang: (lang) => set({ lang }),
  setAudio: (audio) => set({ audio }),
  toggle: (k) => set((s) => ({ [k]: !s[k] }) as Partial<UIState>),
}));
